"use client";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@universe/ui/components/alert-dialog";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@universe/ui/components/breadcrumb";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@universe/ui/components/dropdown-menu";
import { Separator } from "@universe/ui/components/separator";
import { SidebarTrigger, useSidebar } from "@universe/ui/components/sidebar";
import { ChevronDown, LogOut } from "lucide-react";
import { usePathname, useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";

import { AccountSwitchList } from "@/components/account-switch-list";
import AccountTypeIcon from "@/components/account-type-icon";
import { AppLink } from "@/components/app-link";
import { NoTranslate } from "@/components/no-translate";
import S21RiskBanner from "@/components/s21-risk-banner";
import { Route } from "@/enums/route";
import { useDashboardTabs } from "@/hooks/useDashboardTabs";
import { useLogout } from "@/hooks/useLogout";
import { useCustomerAccount } from "@/providers";
import { useRecipientDraftStore } from "@/stores/recipientDraft/useRecipientDraftStore";
import { getInitials } from "@/utils";
import { getBreadcrumbTrail } from "@/utils/breadcrumbTrail";
import type { CustomerAccountUser } from "@generated/types";

export default function BreadCrumb() {
  const {
    customerAccount,
    customerAccounts,
    changeCustomerAccount,
    setIsSwitchingAccount,
    isSwitchingAccount,
    customerAccountId,
    userProfile,
  } = useCustomerAccount();
  const { isMobile, setOpenMobile } = useSidebar();
  const { resetTab } = useDashboardTabs();
  const logout = useLogout();
  const router = useRouter();
  const pathname = usePathname();
  const t = useTranslations("nav");

  // An unsent recipient draft is lost by an account switch (the switch
  // navigates to the dashboard, which unmounts the wizard), so ask first.
  const isRecipientDraftDirty = useRecipientDraftStore(
    (state) => state.isDirty
  );
  const [accountAwaitingConfirm, setAccountAwaitingConfirm] =
    useState<CustomerAccountUser | null>(null);

  // Track pending account switch during navigation
  const pendingAccountSwitch = useRef<string | null>(null);
  const pendingAccountName = useRef<string | null>(null);
  const [triggerAccountSwitch, setTriggerAccountSwitch] = useState(false);

  // Navigate to Dashboard and switch account
  useEffect(() => {
    if (
      triggerAccountSwitch &&
      pathname === Route.Dashboard &&
      !isSwitchingAccount
    ) {
      setIsSwitchingAccount(true);
      changeCustomerAccount(
        pendingAccountSwitch.current,
        pendingAccountName.current || undefined
      );
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Reset trigger flag after account switch
      setTriggerAccountSwitch(false);
    }
  }, [
    triggerAccountSwitch,
    changeCustomerAccount,
    pendingAccountSwitch,
    pendingAccountName,
    isSwitchingAccount,
    pathname,
    setIsSwitchingAccount,
  ]);

  const switchAccount = (account: CustomerAccountUser) => {
    if (isMobile) {
      setOpenMobile(false);
    }

    if (pathname === Route.Dashboard) {
      // The dashboard tab is per-account state living in the URL,
      // and this branch switches in place without navigating —
      // `usePathname()` has no query string, so `/?tab=pending`
      // lands here. Drop the tab so the new account opens on the
      // default view instead of inheriting the previous one's.
      resetTab();
      setIsSwitchingAccount(true);
      changeCustomerAccount(
        account.id ?? null,
        account.customerAccount?.name || ""
      );
      window.scrollTo({ top: 0, behavior: "instant" });
    } else {
      router.push(Route.Dashboard);
      pendingAccountSwitch.current = account.id ?? null;
      pendingAccountName.current = account.customerAccount?.name || "";
      setTriggerAccountSwitch(true);
    }
  };

  const {
    section: currentSection,
    newRecipient,
    subsection,
  } = getBreadcrumbTrail(pathname);

  return (
    <header className="shrink-0 sticky top-0 z-4 bg-white/100 backdrop-blur-sm">
      <S21RiskBanner />
      <div className="flex h-16 items-center gap-2 px-4 py-4 justify-between overflow-visible">
        <div className="flex items-center gap-1">
          <SidebarTrigger className="-ml-2 lg:ml-0" size="lg" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <Breadcrumb className="-ml-3">
            <BreadcrumbList>
              <BreadcrumbItem>
                <AppLink
                  className="font-semibold text-gray-800 text-sm"
                  href={`/${currentSection}`}
                >
                  {!currentSection ? t("dashboard") : t(currentSection)}
                </AppLink>
              </BreadcrumbItem>
              {newRecipient ? (
                <>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <AppLink
                      className="font-regular text-gray-800 text-sm"
                      href={Route.NewRecipient}
                    >
                      {t("new")}
                    </AppLink>
                  </BreadcrumbItem>
                  {newRecipient.step && (
                    <>
                      <BreadcrumbSeparator />
                      <BreadcrumbItem>
                        <BreadcrumbPage className="font-regular text-gray-800 text-sm">
                          {t(newRecipient.step)}
                        </BreadcrumbPage>
                      </BreadcrumbItem>
                    </>
                  )}
                </>
              ) : (
                subsection && (
                  <>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                      <BreadcrumbPage className="font-regular text-gray-800 text-sm">
                        {subsection}
                      </BreadcrumbPage>
                    </BreadcrumbItem>
                  </>
                )
              )}
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        {/* Customer Account Avatar and Account Switcher */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="group/brand-hover relative flex items-center gap-2 px-2 py-1 md:py-1.5 md:gap-2.5 rounded-full backdrop-blur-sm border border-white/30 transition-all duration-200 shadow-md hover:cursor-pointer text-sm lg:mr-3"
              style={{
                background:
                  "linear-gradient(to bottom right, oklch(from var(--header-button-color, var(--brand-primary)) l c h / 0.9), oklch(from var(--header-button-color, var(--brand-primary)) l c h / 0.8))",
              }}
            >
              {/* Hover gradient overlay */}
              <div className="brand-hover-overlay rounded-full z-[1]" />
              {/* Mobile: Show initials */}
              <div
                className="relative z-[2] flex md:hidden items-center justify-center w-7 h-7 rounded-full font-semibold text-sm shadow-md"
                style={{
                  backgroundColor:
                    "var(--header-button-icon-background, var(--brand-primary))",
                  color: "var(--menu-font-color)",
                  textShadow:
                    "-0.5px 0.5px 1px color-mix(in oklch, var(--menu-font-shadow) 20%, transparent), 0 0.5px 2px color-mix(in oklch, var(--menu-font-shadow) 40%, transparent), 1px 1px 0px color-mix(in oklch, var(--menu-font-shadow) 40%, transparent)",
                }}
              >
                {getInitials(customerAccount?.customerAccount?.name)}
              </div>
              {/* Desktop: Show account type icon */}
              <div
                className="relative z-[2] hidden md:flex items-center justify-center w-7 h-7 rounded-full shadow-md"
                style={{
                  backgroundColor:
                    "var(--header-button-icon-background, var(--brand-primary))",
                }}
              >
                <AccountTypeIcon
                  type={customerAccount?.customerAccount?.accountType || ""}
                  label=""
                  showLabel={false}
                  size={16}
                  iconClassName=""
                  iconStyle={{
                    color: "var(--menu-font-color)",
                    filter:
                      "drop-shadow(0 0px 1px color-mix(in oklch, var(--menu-font-shadow) 40%, transparent)) drop-shadow(0 0px 1px color-mix(in oklch, var(--menu-font-shadow) 60%, transparent))",
                  }}
                />
              </div>
              <span
                data-testid="customer-account-username"
                className="relative z-[2] hidden md:block text-base font-medium lg:max-w-full truncate"
                style={{
                  color: "var(--menu-font-color)",
                  textShadow:
                    "-0.5px 0.5px 1px color-mix(in oklch, var(--menu-font-shadow) 20%, transparent), 0 0.5px 2px color-mix(in oklch, var(--menu-font-shadow) 40%, transparent), 1px 1px 0px color-mix(in oklch, var(--menu-font-shadow) 40%, transparent)",
                }}
              >
                <NoTranslate>
                  {customerAccount?.customerAccount?.name}
                </NoTranslate>
              </span>
              <ChevronDown
                className="relative z-[2] h-4 w-4 md:h-5 md:w-5"
                style={{
                  color: "var(--menu-font-color)",
                  filter:
                    "drop-shadow(0 0px 1px color-mix(in oklch, var(--menu-font-shadow) 40%, transparent)) drop-shadow(0 0px 1px color-mix(in oklch, var(--menu-font-shadow) 60%, transparent))",
                }}
              />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-64 rounded-lg drop-shadow-md"
            side="bottom"
            align="end"
            sideOffset={8}
          >
            <div className="px-2 py-2">
              <div className="grid text-left text-sm leading-tight gap-0.5">
                <span className="text-xs text-muted-foreground font-medium">
                  {t("loggedInAs")}
                </span>
                <span
                  className="truncate font-bold text-gray-900 text-base"
                  data-testid="user-full-name"
                >
                  <NoTranslate>{userProfile?.fullName}</NoTranslate>
                </span>
              </div>
            </div>
            <DropdownMenuSeparator className="my-1" />
            <AccountSwitchList
              accounts={customerAccounts ?? []}
              activeAccountId={customerAccountId}
              onSelect={(account) => {
                if (isRecipientDraftDirty) {
                  setAccountAwaitingConfirm(account);
                  return;
                }
                switchAccount(account);
              }}
            />
            <DropdownMenuSeparator className="my-2" />
            <DropdownMenuItem
              className="hover:bg-brand-primary/10 hover:cursor-pointer font-medium"
              onClick={() => logout()}
            >
              <LogOut className="text-gray-700" />
              <span>{t("logout")}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <AlertDialog
          open={accountAwaitingConfirm !== null}
          onOpenChange={(open) => {
            if (!open) setAccountAwaitingConfirm(null);
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t("discardRecipientDraft.title")}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t("discardRecipientDraft.description")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="recipient-draft-switch-cancel">
                {t("discardRecipientDraft.cancel")}
              </AlertDialogCancel>
              <AlertDialogAction
                data-testid="recipient-draft-switch-confirm"
                onClick={() => {
                  const account = accountAwaitingConfirm;
                  setAccountAwaitingConfirm(null);
                  if (account) switchAccount(account);
                }}
              >
                {t("discardRecipientDraft.confirm")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </header>
  );
}
